import axios from 'axios';
import {get} from '@/utils/local';
import router from "@/router";
import {clearCache} from '@/services/user.service';
import { applyTenantHeader } from '@/utils/tenant';
import { extractTraceId, showTraceError } from '@/utils/trace-error';
import { isReadonlyPath, isWriteRequest } from '@/utils/menu-access';
import { Message } from 'element-ui';

let baseUrl = process.env.VUE_APP_BASE_URL;
let maxTime = process.env.MAX_REQ_TIME;

if (process.env.NODE_ENV === 'production') {
    baseUrl = window.globalConfig.rootUrl;
}


// 创建请求服务
const serves = axios.create({
    baseURL: baseUrl,
    timeout: maxTime // 请求超时时间
})

// 设置请求发送之前的拦截器
serves.interceptors.request.use(config => {
    // 统一在http请求的header都加上token
    let token = get('token');
    if (token && token.access_token) {
        config.headers.Authorization = `Bearer ${token.access_token}`
    }
    applyTenantHeader(config);

    // 当前菜单被授权为只读时，兜底拦截写请求。
    // 主防线是按钮置灰（readonly-guard），这里防的是漏网的写操作入口。
    const currentPath = router.currentRoute ? router.currentRoute.path : '';
    if (isReadonlyPath(currentPath) && isWriteRequest(config.url, config.method)) {
        Message.warning('当前角色对该菜单为只读权限，不可操作');
        return Promise.reject(new Error('READONLY_MENU'));
    }

    // 带上当前菜单路径，供后端后续做按菜单的写操作硬校验
    config.headers['__menu'] = encodeURIComponent(currentPath || '');
    return config
}, err => {Promise.reject(err)})

// 设置响应拦截器
serves.interceptors.response.use(response => {
    // 院内字典请求由医生电脑代转，保留 HIS 原始状态交给院内服务确认保存结果。
    if (response.config.hdisRelayResponse) return { status: response.status, body: response.data };
    // 根据响应状态码判断是否请求成功
    //console.log(JSON.stringify(response));
    if (response.status === 200 || response.status === 204) {
        // 即使返回空数据（null、undefined、空数组），也正常返回，不触发错误
        // 空数据是正常的业务场景，不应该触发错误处理
        return Promise.resolve(response.data)
    } else {
        // 如果请求失败，则统一处理错误
        return Promise.reject(response)
    }
}, err => {
    if (err.config && err.config.hdisRelayResponse) return Promise.reject(err);
    // 只读拦截在请求拦截器里已经提示过，这里直接静默透传，避免再弹一次错误框
    if (err && err.message === 'READONLY_MENU') {
        return Promise.reject('当前角色对该菜单为只读权限，不可操作');
    }
    // 取出状态码和消息
    //console.log(JSON.stringify(err));
    let message = '';
    let status = getStatus(err);
    console.log('status', status);
    // 检查是否是真正的401错误（有明确的401响应状态码），而不是网络错误
    // 网络错误不应该直接跳转登录，因为可能是临时网络问题
    const isReal401 = err.response && err.response.status === 401;
    const isNetworkError = err.message && err.message.indexOf("Network Error") != -1;
    
    // 调试日志：记录错误信息，帮助排查问题
    if (isReal401 || isNetworkError) {
        console.warn('请求错误详情:', {
            status,
            isReal401,
            isNetworkError,
            responseStatus: err.response?.status,
            responseData: err.response?.data,
            message: err.message,
            url: err.config?.url
        });
    }
    
    let msg = err.response && err.response.data && err.response.data.error ? (
        err.response.data.error.code ?  err.response.data.error.code : err.response.data.error.details ? err.response.data.error.details :
        err.response.data.error.message ? err.response.data.error.message :
        err.response.data.error_description ? err.response.data.error_description :
        status + '错误') : status + '错误';

    // 提取 TraceId
    let traceId = extractTraceId(err);

    // 统一处理"未登录"：标准 401，或后端以业务异常形式返回的"用户未登录"
    // 后者 HTTP 状态是 403/500（ABP UserFriendlyException），不会进 401 分支，
    // 旧逻辑会弹一个带链路ID的错误框；这里改为清缓存 + 跳登录页，不弹错误提示。
    const isNotLoggedIn = isReal401 || (typeof msg === 'string' && msg.indexOf('用户未登录') !== -1);
    if (isNotLoggedIn) {
        clearCache();
        if (router.currentRoute.path !== '/login') {
            router.push('/login');
        }
        return Promise.reject(msg || '用户未登录');
    }

    // 判断请求时发生的错误类型，处理不同的错误
    switch (status) {
        case 401:
            // 真正的 401 已在上方统一处理，这里只剩网络错误/其它情况
            if (isNetworkError) {
                // 网络错误不跳转登录，只提示错误
                message = '网络连接错误，请检查网络后重试';
            } else {
                message = '无访问权限';
            }
            break;
        case 406:
            message = '应用未激活或已过有效期';
            router.push('/app-register');
            break;
        case 403:
            message = msg ? msg :'请求错误';
            break;
        case 415:
            message = '参数格式错误';
            break;
        case 500:
            message = '接口发生错误';
            break;
        case 504:
            message = '请求超时';
            break;
        default:
            message = msg ? msg : `请求错误`;
            break;
    }
    showTraceError(message, traceId, status);
    return Promise.reject(message);
})

function getStatus(err){
    // 网络错误不应该返回401，应该返回特定的网络错误码
    // 但为了保持兼容性，仍然返回401，但在上层逻辑中会区分处理
    if (err.message && err.message.indexOf("Network Error") != -1) {
        return 401; // 标记为401，但会在上层判断是否为真正的401
    }
    if(err.code && err.code == 'ECONNABORTED'){
        return 504;
    }
    let status = err.response && err.response.status ? err.response.status : 403;
    return status;
}

export { serves as default, baseUrl }
